import { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import camelcase from 'camelcase';
import i18n from 'i18next';
import { useTheme as useMUITheme, useMediaQuery } from '@mui/material';

import { Async } from 'modules/Licenses/LicensesSlice';
import { defaultErrorToast } from 'modules/Utils';
import { useNonInitialEffect } from 'modules/Core/Hooks';
import { fieldNameToKey, fieldNamesMap } from './../LicenseSchema';
import { Row, Col, Title, FormGroup, Label, Number } from 'modules/Core/Common';
import { LicenseAmountSkeleton } from './LicenseAmountSkeleton.component';
import { CEFR_NAME, MCER_NAME } from 'constants';
import emptyDataIcon from 'assets/layout/empty-data/test-icon.svg?url';

import * as S from './LicenseAmount.styles';

const labelTooltipStyle = {
  maxWidth: '100%',
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
};

export const LicenseAmount = ({
  studentId,
  license,
  showLicensesAvailable,
  testType,
  onCustomCategoriesAmountChange,
  handleChangeValue,
  onMountProctoringFields,
}) => {
  const [amountFields, setAmountFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const [availablesPluralText, setAvailablesPluralText] = useState(
    i18n.t('modules.licenses.licenses-available')
  );
  const fullTestNames = [CEFR_NAME, MCER_NAME];

  const dispatch = useDispatch();

  const muiTheme = useMUITheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.only('xs'));

  useEffect(() => {
    if (license?.testId || license?.customTestId) {
      // licenseId from student license and license?.id from licenses module
      loadTestAmounts(license?.licenseId || license?.id);
    }
    if (license.availableAmount != 1) {
      setAvailablesPluralText(i18n.t('modules.licenses.licenses-available'));
    }
  }, [license.testId, license.customTestId]);

  useNonInitialEffect(() => {
    resetAmounts();
  }, [license.testId, license.customTestId]);

  const onChangeAmountField = (
    value,
    fieldName,
    categoryId,
    license = null
  ) => {
    if (categoryId && license) {
      let updated = [...license.customCategoriesAmount];

      const current = {
        amount: value,
        category_id: categoryId,
      };

      const index = license.customCategoriesAmount.findIndex(
        (item) => item.category_id === current.category_id
      );

      if (index !== -1) {
        updated[index] = current;
      } else {
        updated.push(current);
      }

      handleChangeValue('customCategoriesAmount', updated);
      return;
    }
    const fieldKey = fieldNameToKey(fieldName);
    handleChangeValue(fieldKey, value);
  };

  const setDefaultAmountsAfterLoading = (values) => {
    Object.keys(values).forEach((content) => {
      values[content].forEach((item) => {
        if (item.category_id) {
          return handleCustomCategoryChange({
            amount: item.amount,
            category_id: item.category_id,
          });
        }
        const fieldKey = fieldNameToKey(item.field_name);
        return handleChangeValue(fieldKey, item.amount);
      });
    });
  };

  const resetAmounts = () => {
    Object.keys(fieldNamesMap).forEach((fieldName) => {
      handleChangeValue(fieldNamesMap[fieldName], 0);
    });
    if (onCustomCategoriesAmountChange) onCustomCategoriesAmountChange([]);
  };

  const loadTestAmounts = (id) => {
    setLoading(true);
    const action = Async.getLicenseTestAmount({
      id,
      studentId,
      testId: license.testId || license.customTestId,
      typeTest: testType,
      selectedGroup: !!license.groupId,
      onSuccess: ({ data }) => {
        setAmountFields(data.content.license_amount);
        setDefaultAmountsAfterLoading(data.content.license_amount);
        onMountProctoringFields(data.content.proctoring);
        setLoading(false);
      },
      onError: (e) => {
        defaultErrorToast(
          i18n.t(
            'errors.error-sorry-an-error-occurred-during.get-new-license-test-amount'
          )
        )(e);
        setLoading(false);
      },
    });

    dispatch(action);
  };

  const getLicenseAmountFieldValue = (fieldName, categoryId) => {
    if (categoryId) {
      const category = license?.customCategoriesAmount?.find(
        (category) => +category?.category_id === +categoryId
      );

      return category?.amount !== undefined ? category.amount : '';
    }

    const field = license?.[camelcase(fieldName)];

    return field !== undefined ? field : '';
  };

  const handleCustomCategoryChange = (category) => {
    const categoryExists = license?.customCategoriesAmount?.some(
      (currentCategory) =>
        +currentCategory?.category_id === +category?.category_id
    );

    if (categoryExists) {
      const updatedCategoriesAmount = license?.customCategoriesAmount?.map(
        (currentCategory) => {
          if (+currentCategory?.category_id === +category?.category_id) {
            return category;
          }

          return currentCategory;
        }
      );

      return onCustomCategoriesAmountChange(updatedCategoriesAmount);
    }

    onCustomCategoriesAmountChange([
      ...license.customCategoriesAmount,
      category,
    ]);
  };

  if (loading) {
    return (
      <>
        <S.FormHeader
          style={{ margin: license?.hasAntiFraud ? '1.6rem 0' : '0 0 1.6rem' }}
        >
          <Title size="quartenary" tag="h2">
            {i18n.t('modules.licenses.license-amount')}
          </Title>
        </S.FormHeader>

        <LicenseAmountSkeleton />
      </>
    );
  }

  return (
    <>
      <S.FormHeader
        style={{ margin: license?.hasAntiFraud ? '1.6rem 0 0' : '0' }}
      >
        <Title size="quartenary" tag="h2">
          {i18n.t('modules.licenses.license-amount')}
        </Title>
        {showLicensesAvailable && license?.availableAmount !== 0 && (
          <span>
            <strong>{license?.availableAmount}</strong> {availablesPluralText}
          </span>
        )}
      </S.FormHeader>

      {!license?.testId && !license?.customTestId && (
        <S.EmptyData>
          <img src={emptyDataIcon} alt="empty-data-test-icon" />
          <div>
            <p>
              {i18n.t(
                'modules.licenses.license-forms.license-amount.empty-data.message'
              )}
            </p>
            <span>
              {i18n.t(
                'modules.licenses.license-forms.license-amount.empty-data.warning'
              )}
            </span>
          </div>
        </S.EmptyData>
      )}

      {!!amountFields?.simulation?.length && (
        <Title
          size="default"
          tag="h6"
          style={{ margin: isMobile ? '1.6rem 0 1.6rem' : '3.2rem 0 1.6rem' }}
        >
          {i18n.t('common-words.tests')}
        </Title>
      )}

      <Row>
        {amountFields?.simulation &&
          amountFields.simulation.map((field, index) => (
            <Col xs={6} sm={2} key={index}>
              <FormGroup>
                <Label
                  showTooltip={field.name.length > 17}
                  style={labelTooltipStyle}
                >
                  {fullTestNames.includes(field.name)
                    ? 'Full Test'
                    : field.name}
                </Label>
                <Number
                  id={camelcase(field.field_name)}
                  name={camelcase(field.field_name)}
                  type="number"
                  onChange={(value) =>
                    onChangeAmountField(
                      value,
                      field.field_name,
                      field.category_id,
                      license
                    )
                  }
                  onBlur={(event) => {
                    if (!event.target.value) {
                      onChangeAmountField(
                        0,
                        field.field_name,
                        field.category_id
                      );
                    }
                  }}
                  value={getLicenseAmountFieldValue(
                    field.field_name,
                    field.category_id
                  )}
                />
              </FormGroup>
            </Col>
          ))}
      </Row>

      {!!amountFields?.practice?.length && (
        <Title size="default" tag="h6" style={{ marginBottom: '1.6rem' }}>
          {i18n.t('common-words.practice')}
        </Title>
      )}

      <Row>
        {amountFields?.practice &&
          amountFields.practice.map((field, index) => (
            <Col xs={6} sm={2} key={index}>
              <FormGroup>
                <Label
                  showTooltip={field.name.length > 17}
                  style={labelTooltipStyle}
                >
                  {field.name}
                </Label>
                <Number
                  id={camelcase(field.field_name)}
                  name={camelcase(field.field_name)}
                  type="number"
                  onChange={(value) =>
                    onChangeAmountField(
                      value,
                      field.field_name,
                      field.category_id
                    )
                  }
                  onBlur={(event) => {
                    if (!event.target.value) {
                      onChangeAmountField(
                        0,
                        field.field_name,
                        field.category_id
                      );
                    }
                  }}
                  value={getLicenseAmountFieldValue(
                    field.field_name,
                    field.category_id
                  )}
                />
              </FormGroup>
            </Col>
          ))}
      </Row>

      {!!amountFields?.others?.filter((field) => !field.name.includes('Amount'))
        .length && (
        <Title size="default" tag="h6" style={{ margin: '0 0 16px 0' }}>
          {i18n.t('common-words.others')}
        </Title>
      )}

      <Row>
        {amountFields?.others &&
          amountFields.others
            .filter((field) => field.name !== i18n.t('common-words.amount'))
            .map((field, index) => (
              <Col xs={12} sm={2} key={index}>
                <FormGroup>
                  <Label
                    showTooltip={field.name.length > 17}
                    style={labelTooltipStyle}
                  >
                    {field.name}
                  </Label>
                  <Number
                    id={camelcase(field.field_name)}
                    name={camelcase(field.field_name)}
                    type="number"
                    onChange={(value) =>
                      onChangeAmountField(
                        value,
                        field.field_name,
                        field.category_id
                      )
                    }
                    onBlur={(event) => {
                      if (!event.target.value) {
                        onChangeAmountField(
                          0,
                          field.field_name,
                          field.category_id
                        );
                      }
                    }}
                    value={getLicenseAmountFieldValue(
                      field.field_name,
                      field.category_id
                    )}
                  />
                </FormGroup>
              </Col>
            ))}
      </Row>
    </>
  );
};

LicenseAmount.defaultProps = {
  showLicensesAvailable: true,
};

LicenseAmount.propTypes = {
  studentId: PropTypes.number,
  license: PropTypes.object,
  showLicensesAvailable: PropTypes.bool,
  testType: PropTypes.string,
  onCustomCategoriesAmountChange: PropTypes.func,
  handleChangeValue: PropTypes.func,
  onMountProctoringFields: PropTypes.func,
};
